/**
 * Playbooks Alpha — CRM Enrichment.
 *
 * The FE-side config model for the recipe. The portal no longer wires the
 * legacy /playbooks config API; these types live on as building blocks
 * reused by Custom Playbooks (trigger, enrichment, validation, sync).
 *
 * Recipe: WHEN → FIND → ENRICH → VALIDATE → RESEARCH → CREATE. VALIDATE and
 * RESEARCH are optional (off by default); the rest always run.
 */

import type { FilterCondition } from '@/components/smart-columns/advanced-filter-builder.component';
import type { FieldMappingItem } from '@/store/features/crm.feature';
import { INITIAL_FILTERS, type SearchFilters } from '@/types/search-filters';
import {
  INITIAL_COMPANY_FILTERS,
  type CompanySearchFilters,
} from '@/utils/company-search.utils';

export type CrmObjectType = 'Contact' | 'Account' | 'Lead';

/**
 * The WHEN step — a CRM trigger condition. `conditions` reuses the canonical
 * `FilterGroup` (AND/OR + FilterCondition[]) so the payload matches the app's
 * existing filter contract and the backend wiring is a drop-in.
 */
export type SalesforceTriggerFieldType =
  | 'text'
  | 'picklist'
  | 'multipicklist'
  | 'boolean'
  | 'number'
  | 'date';

export interface SalesforceTriggerCondition extends FilterCondition {
  fieldType?: SalesforceTriggerFieldType;
}

export interface SalesforceTriggerGroup {
  /** Clu may write Match any (OR); the editor keeps whichever was saved. */
  operator: 'AND' | 'OR';
  conditions: SalesforceTriggerCondition[];
}

/**
 * The deployed v1 poll is deliberately discriminated to one executable source.
 * Unknown saved CRM/object JSON is normalized at the DTO boundary rather than
 * leaking broad strings into the editor.
 */
export interface CrmTriggerConfig {
  crmType: 'salesforce';
  objectType: 'Account';
  conditions: SalesforceTriggerGroup | null;
}

/**
 * The ENRICH step — which data we resolve per person. `profile` (identity:
 * name/title/company/LinkedIn) is the base and is always included with email or
 * phone; on its own it's 0.5cr. Email adds 1cr, phone adds 10cr — so all three
 * = 11cr ("full"). Costs are pay-for-results (billed only on data found).
 */
export interface EnrichmentConfig {
  profile: boolean;
  email: boolean;
  phone: boolean;
}

/**
 * The VALIDATE step (optional) — verify the data we just enriched. Email uses
 * Emailable (deliverability), phone uses ClearoutPhone (validity / line type);
 * both run as smart columns at 0.5cr each, per lead, only on data we found.
 */
export interface ValidationConfig {
  email: boolean;
  phone: boolean;
}

/**
 * The RESEARCH step (optional) — run the org's saved AI agents on each person,
 * each writing a field. `agentIds` are Custom Agent ids (Agents settings).
 * Empty = step off. Cost is token-driven per agent (shown as an estimate).
 */
export interface AgentsConfig {
  agentIds: string[];
}

/** What we create the found people AS in the CRM (the account is the anchor). */
export type CrmCreateObjectType = 'Contact' | 'Lead';

/**
 * The CREATE step — how found people are written to the CRM. Uses the SAME
 * field-mapping contract as lead-list sync (`FieldMappingItem[]`, per-field
 * write modes). `mappingMode: 'default'` inherits the org's default template +
 * write rules (Integrations); `'custom'` uses `mappings`, scoped to this
 * playbook. Default write mode is fill-if-empty — a blank never overwrites.
 */
export interface CrmSyncConfig {
  objectType: CrmCreateObjectType;
  mappingMode: 'default' | 'custom';
  mappings: FieldMappingItem[];
}

export interface CrmEnrichmentConfig {
  /** Whether the user has added this playbook to "My Playbooks" (via the
   *  template). False = only offered as a template; true = a real playbook row. */
  added: boolean;
  /** Master on/off. Gates the 30-minute auto-poll; manual runs work regardless. */
  isActive: boolean;
  /** The WHEN trigger — CRM + property/operator/value condition(s). */
  trigger: CrmTriggerConfig;
  /** The one editable numeric knob. Cap applied at find, before enrich. */
  maxPeoplePerAccount: number;
  /**
   * The Find step's audience — the FULL people-finder filter set (titles,
   * seniority, location, industry, keywords, …) as the finder's own
   * serializable blobs, so the whole finder panel is reusable here and the run
   * builder consumes the same shape the finder does.
   */
  audienceFilters: SearchFilters;
  audienceCompanyFilters: CompanySearchFilters;
  /** Which enrichment outputs to resolve per person. */
  enrichment: EnrichmentConfig;
  /** Optional — verify email/phone deliverability. Off by default. */
  validation: ValidationConfig;
  /** Optional — saved AI agents to run per person. Empty by default. */
  agents: AgentsConfig;
  /** How the found people are created/written to the CRM. */
  sync: CrmSyncConfig;
}

/** Seed audience from Kindsight's ZoomInfo buying-committee saved search. */
export const SEED_INCLUDE_TITLES: string[] = [
  'Director of Development',
  'Chief Advancement Officer',
  'VP of Philanthropy',
  'Major Gifts Officer',
  'Prospect Research Manager',
  'Annual Giving Director',
  'Executive Director',
];

export const SEED_EXCLUDE_TITLES: string[] = [
  'Assistant',
  'Intern',
  'Coordinator',
  'Volunteer',
];

export const DEFAULT_CRM_ENRICHMENT_CONFIG: CrmEnrichmentConfig = {
  added: false,
  isActive: false,
  trigger: {
    crmType: 'salesforce',
    objectType: 'Account',
    // The trigger must be selected from the connected org's live Salesforce
    // schema. Never seed an org-specific custom field into a shared default.
    conditions: null,
  },
  maxPeoplePerAccount: 10,
  audienceFilters: {
    ...INITIAL_FILTERS,
    jobTitlesInclude: SEED_INCLUDE_TITLES,
    jobTitlesExclude: SEED_EXCLUDE_TITLES,
  },
  audienceCompanyFilters: INITIAL_COMPANY_FILTERS,
  enrichment: { profile: true, email: true, phone: true },
  validation: { email: false, phone: false },
  agents: { agentIds: [] },
  sync: { objectType: 'Contact', mappingMode: 'default', mappings: [] },
};

/** Per-output credit costs. Profile is the base (0.5cr) but is included free
 * with email or phone; email +1cr, phone +10cr. All three = 11cr ("full"). */
export const ENRICHMENT_COSTS = {
  profileOnly: 0.5,
  email: 1,
  phone: 10,
} as const;

/** Max per-person credit cost for the selected enrichment outputs. */
export function enrichmentCostPerPerson(e: EnrichmentConfig): number {
  const email = e.email ? ENRICHMENT_COSTS.email : 0;
  const phone = e.phone ? ENRICHMENT_COSTS.phone : 0;
  // Profile is 0.5 only when it's the sole output; otherwise it's included free.
  const profileSolo =
    e.profile && !e.email && !e.phone ? ENRICHMENT_COSTS.profileOnly : 0;
  return email + phone + profileSolo;
}

/** Per-check validation costs (Emailable / ClearoutPhone), per lead. */
export const VALIDATION_COSTS = {
  email: 0.5,
  phone: 0.5,
} as const;

/** Max per-person credit cost for the selected validation checks. */
export function validationCostPerPerson(v: ValidationConfig): number {
  return (
    (v.email ? VALIDATION_COSTS.email : 0) +
    (v.phone ? VALIDATION_COSTS.phone : 0)
  );
}

export const PLAYBOOK_IMPORT_COST_PER_PERSON = 0.5;

/**
 * Max per-person credit cost for a full CRM Enrichment run — enrichment
 * outputs + validation checks + saved agents + the persisted person import.
 * Salesforce Account lookups are free. This is what the gallery shows as
 * "cost / row". `agentsCostPerPerson` is the selected agents' summed price
 * from the org's per-agent prices (`agentsCostPerRecord`); agents carry no
 * fixed price here.
 */
export function crmEnrichmentCostPerPerson(
  config: CrmEnrichmentConfig,
  agentsCostPerPerson: number
): number {
  return (
    PLAYBOOK_IMPORT_COST_PER_PERSON +
    enrichmentCostPerPerson(config.enrichment) +
    validationCostPerPerson(config.validation) +
    agentsCostPerPerson
  );
}
